'use client';

import { c as _c } from "react/compiler-runtime";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, Check, ChevronDown, ChevronLeft, ChevronRight, Copy, CornerDownRight, ExternalLink, History, Loader2, MessageCircleMore, Pencil, RotateCcw, Search, Square, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { useQueryStates, parseAsString } from 'nuqs';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { useClipboard_CopySDK } from '~/modules/browser/clipboard/components';
import { cjk } from '@streamdown/cjk';
import { code } from '@streamdown/code';
import { math } from '@streamdown/math';
import { mermaid } from '@streamdown/mermaid';
import { Streamdown } from 'streamdown';
import type { LinkSafetyConfig, LinkSafetyModalProps } from 'streamdown';
import 'streamdown/styles.css';
import type { UIMessage } from 'ai';
import { isToolUIPart, getToolName } from 'ai';
import { cn } from '~/utils';
import { useLatest } from '~/utils/react/refs';
import { useI18n } from '~/features/i18n';
import { Auth } from '~/modules/auth/client';
import { usePostHog } from 'posthog-js/react';
import { useActivityAiActiveChat } from '../useActivityAiActiveChat';
import { captureAnswerRating, type AiSearchAnswerRating } from '../ai-search-analytics';
import { usePendingPromptAutoStart, useFollowupAutoSend } from '../activity-ai-chat-runtime-store';
import { useActivityAiFilterChips } from './useActivityAiFilterChips';
import { useActivitySearchCtx } from './context';
import { DRAWER_SECTION_BY_CHIP_ID } from './ai-filter-chips';
import { useApplyAiResult } from './ActivityAiResultsBridge';
import { useAutoScrollToBottom } from './useAutoScrollToBottom';
import { usePendingPromptQueue } from './usePendingPromptQueue';
import { sanitizeAssistantText } from './sanitizeAssistantText';
import { ChatSpendingBadge } from './ChatContextBadge';
import { ActivityAiHistoryDrawer } from './ActivityAiHistoryDrawer';
import { ActivityAiResultFilterChips } from './ActivityAiResultFilterChips';
import { useActivityAiChatStore } from '../activity-ai-chat-store';
import { collapseSupersededAssistantTurns } from '../activity-ai-search-turn';
import { RecentChatsList } from '../RecentChatsList';
import { SearchActivitiesOutputSchema, type SearchActivitiesFilters } from '@funkey/ai-tools/schemas';
import { extractQuotePreview, type QuotePreview } from '../quote-reply';
import { AI_SEARCH_EVENTS, useAiSearchAnalytics } from '../ai-search-analytics';
import { shouldShowLongConversationNotice } from '../long-conversation-notice';
const activityAiStreamdownPlugins = {
  cjk,
  code,
  math,
  mermaid
};
const activityAiStreamdownAnimate = {
  animation: 'fadeIn' as const,
  sep: 'word' as const,
  duration: 220,
  stagger: 14,
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
};

// Streamdown's built-in link-safety modal renders without a portal, so it gets
// trapped in the sticky sidebar's stacking context and painted under the
// results grid (FUN-4961). Radix's Dialog portals to document.body instead.
const ActivityAiExternalLinkModal: React.FC<LinkSafetyModalProps> = props => {
  const $ = _c(46);
  const i18n = useI18n();
  let t0;
  if ($[0] !== props.url) {
    t0 = () => props.url;
    $[0] = props.url;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const copySDK = useClipboard_CopySDK(t0);
  const t1 = props.isOpen;
  let t2;
  if ($[2] !== props) {
    t2 = open => {
      if (!open) {
        props.onClose();
      }
    };
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ExternalLink className="size-4 shrink-0" strokeWidth={2.25} aria-hidden={true} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== i18n) {
    t4 = i18n._("activity.search.ai.externallink.title", undefined, {
      message: "Open external link?"
    });
    $[5] = i18n;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <DialogTitle className="flex items-center gap-2">{t3}{t4}</DialogTitle>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== i18n) {
    t6 = i18n._("activity.search.ai.externallink.warning", undefined, {
      message: "You're about to visit an external website."
    });
    $[9] = i18n;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <DialogDescription>{t6}</DialogDescription>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <DialogHeader>{t5}{t7}</DialogHeader>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== props.url) {
    t9 = <div className="rounded-md bg-stone-100 p-3 font-mono text-xs break-all text-blue-900">{props.url}</div>;
    $[16] = props.url;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== copySDK.isCopied) {
    t10 = copySDK.isCopied ? <Check className="size-3.5" strokeWidth={2.25} aria-hidden={true} /> : <Copy className="size-3.5" strokeWidth={2.25} aria-hidden={true} />;
    $[18] = copySDK.isCopied;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== copySDK.isCopied || $[21] !== i18n) {
    t11 = copySDK.isCopied ? i18n._("activity.search.ai.externallink.copied", undefined, {
      message: "Copied!"
    }) : i18n._("activity.search.ai.externallink.copy", undefined, {
      message: "Copy link"
    });
    $[20] = copySDK.isCopied;
    $[21] = i18n;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== copySDK.onCopy || $[24] !== t10 || $[25] !== t11) {
    t12 = <Button type="button" variant="outline" onClick={copySDK.onCopy}>{t10}{t11}</Button>;
    $[23] = copySDK.onCopy;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== props) {
    t13 = () => {
      props.onConfirm();
      props.onClose();
    };
    $[27] = props;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ExternalLink className="size-3.5" strokeWidth={2.25} aria-hidden={true} />;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== i18n) {
    t15 = i18n._("activity.search.ai.externallink.open", undefined, {
      message: "Open link"
    });
    $[30] = i18n;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t13 || $[33] !== t15) {
    t16 = <Button type="button" onClick={t13}>{t14}{t15}</Button>;
    $[32] = t13;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t12 || $[36] !== t16) {
    t17 = <DialogFooter>{t12}{t16}</DialogFooter>;
    $[35] = t12;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t17 || $[39] !== t8 || $[40] !== t9) {
    t18 = <DialogContent className="sm:max-w-md" data-testid="ai-chat-external-link-modal">{t8}{t9}{t17}</DialogContent>;
    $[38] = t17;
    $[39] = t8;
    $[40] = t9;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== props.isOpen || $[43] !== t18 || $[44] !== t2) {
    t19 = <Dialog open={t1} onOpenChange={t2}>{t18}</Dialog>;
    $[42] = props.isOpen;
    $[43] = t18;
    $[44] = t2;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  return t19;
};
const activityAiLinkSafety: LinkSafetyConfig = {
  enabled: true,
  renderModal: props => <ActivityAiExternalLinkModal {...props} />
};

/**
 * AI-SDK-Elements-style shimmer placeholder shown while the model is
 * "thinking" — between submit and the first text/tool delta. Three skeleton
 * bars with a moving highlight (see the `animate-chat-shimmer` utility in
 * `globals.css`). Replaces the prior spinner row.
 */
const ChatShimmerLoader: React.FC<{
  label: string;
}> = t0 => {
  const $ = _c(8);
  const {
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-[11px] text-blue-900/50">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="h-2.5 w-4/5 animate-chat-shimmer rounded-full" aria-hidden={true} />;
    t3 = <span className="h-2.5 w-3/5 animate-chat-shimmer rounded-full" aria-hidden={true} />;
    t4 = <span className="h-2.5 w-2/5 animate-chat-shimmer rounded-full" aria-hidden={true} />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== label || $[6] !== t1) {
    t5 = <output className="flex w-full max-w-[90%] flex-col gap-2 rounded-3xl rounded-tl-md bg-stone-100 px-3 py-2.5" aria-label={label}>{t1}{t2}{t3}{t4}</output>;
    $[5] = label;
    $[6] = t1;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};

/**
 * Collapsed reasoning block — mirrors AI-SDK-Elements' `Reasoning` component.
 * The chat route already streams `reasoning-delta` parts; this renders them
 * inside a `<details>` so they're hidden by default but inspectable.
 *
 * Auto-opens while the reasoning is actively streaming (no closing
 * `reasoning-end` yet) so the user sees the model think live; auto-collapses
 * once it's done. The `open` attribute can still be toggled manually.
 */
const ReasoningBlock: React.FC<{
  text: string;
  isStreaming: boolean;
}> = t0 => {
  const $ = _c(12);
  const {
    text,
    isStreaming
  } = t0;
  const i18n = useI18n();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChevronRight className="size-3 transition-transform group-open/reasoning:rotate-90" aria-hidden={true} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== i18n || $[2] !== isStreaming) {
    t2 = isStreaming ? i18n._({
      id: "activity.search.ai.reasoning.streaming",
      message: "Reasoning\u2026"
    }) : i18n._({
      id: "activity.search.ai.reasoning.show",
      message: "Show reasoning"
    });
    $[1] = i18n;
    $[2] = isStreaming;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3 py-1.5 font-medium text-blue-900/60 hover:text-blue-900/80">{t1}<span>{t2}</span></summary>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== text) {
    t4 = <div className="px-3 pb-2 leading-relaxed break-words whitespace-pre-wrap">{text}</div>;
    $[6] = text;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isStreaming || $[9] !== t3 || $[10] !== t4) {
    t5 = <details open={isStreaming} data-testid="ai-chat-reasoning" className="group/reasoning rounded-2xl border border-blue-900/10 bg-blue-50/40 text-[11px] text-blue-900/70">{t3}{t4}</details>;
    $[8] = isStreaming;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};

/** Max composer height (px) before it stops growing and scrolls internally. */
const COMPOSER_MAX_HEIGHT_PX = 200;

/**
 * Chat conversation view — wired to the AI SDK `useChat` (via `useActivityAiActiveChat`).
 *
 * - Empty state: recent-chats landing.
 * - Active state: streaming assistant text (Streamdown) + a spinner row while
 *   the `search_activities` tool runs. Activity cards are NOT rendered in chat —
 *   the page-level results grid is the source of truth, deriving its own id set
 *   straight from `chat.messages` (`useSelectedSearchTurn`, ADR 0003). Mobile
 *   adds a "Show all N results" button that dismisses the drawer.
 */
export const ActivityAiChatPanel: React.FC<{
  className?: string;
  /** @deprecated The shared header now owns the back button. */
  hideBackButton?: boolean;
  autoFocusOnMount?: boolean;
  /** "mobile" enables the "Show all N results" hand-off to the grid. */
  variant?: 'desktop' | 'mobile';
  /** Mobile-only: dismisses the drawer to reveal the underlying results grid. */
  onCloseDrawer?: () => void;
  /** Optional: opens the in-panel history drawer (header trigger handled here). */
  historyOpen?: boolean;
  historyHeaderInParent?: boolean;
  onOpenHistory?: () => void;
  onCloseHistory?: () => void;
}> = ({
  className,
  autoFocusOnMount = true,
  variant = 'desktop',
  onCloseDrawer,
  historyOpen,
  historyHeaderInParent = false,
  onOpenHistory,
  onCloseHistory
}) => {
  const i18n = useI18n();
  const analytics = useAiSearchAnalytics();
  const session = Auth.useSession();
  const isAdmin = session.user?.isSuperAdmin === true;
  const {
    uiMessages,
    sendMessage,
    status,
    stop,
    chatId,
    isHydrating,
    reloadMessages,
    openThread,
    editAndResend,
    regenerate,
    replyToMessage,
    startNewChat,
    goToAllChats,
    error
  } = useActivityAiActiveChat();
  // Structured rate-limit detail (which window, its cap, reset time) parsed from
  // the 429 body so the error bubble can show a live countdown + the cap.
  const rateLimitInfo = error && isRateLimitError(error) ? parseRateLimitInfo(error) : null;
  // Live filter state, read here so a sent turn can (a) carry the tool-mappable filters
  // to the model as context and (b) snapshot the full applied-chip set onto the user
  // message — computed at submit time, no effect/holder needed.
  const searchFilters = useActivitySearchCtx().useFilters();
  const appliedFilterChips = useActivityAiFilterChips();
  const threads = useActivityAiChatStore(s => s.threads);
  const sortedThreads = useMemo(() => Object.values(threads).sort((a, b) => b.createdAt - a.createdAt), [threads]);
  const [draft, setDraft] = useState('');
  // A `?prompt=` deep link (set by `ActivityAiAutoPrompt`) starts a fresh chat
  // immediately — same as clicking a hero suggestion — instead of only
  // prefilling the composer.
  usePendingPromptAutoStart(startNewChat);
  /** When set, the textarea is editing a previous user prompt instead of composing a new one. */
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  /** When set, the next user submit will quote this message instead of being a plain follow-up. */
  const [replyingTo, setReplyingTo] = useState<QuotePreview | null>(null);
  const scrollerRef = useAutoScrollToBottom<HTMLDivElement>([uiMessages, status]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Controlled-vs-internal history drawer: if the parent doesn't pass props,
  // manage state locally so the drawer still works (used by desktop sidebar
  // when no shared header lifts the state).
  const [internalHistoryOpen, setInternalHistoryOpen] = useState(false);
  const historyVisible = historyOpen !== undefined ? historyOpen : internalHistoryOpen;
  const closeHistory = () => {
    if (onCloseHistory) onCloseHistory();else setInternalHistoryOpen(false);
  };
  // When the parent owns history state it fires its own HISTORY_OPENED (with its
  // source); only track here for the panel-internal drawer.
  const openHistory = () => {
    if (onOpenHistory) {
      onOpenHistory();
    } else {
      analytics[AI_SEARCH_EVENTS.HISTORY_OPENED]({
        session_id: chatId || null,
        source: 'chat_panel'
      });
      setInternalHistoryOpen(true);
    }
  };
  const isStreaming = status === 'submitted' || status === 'streaming';
  // Collapse retry runs BEFORE filtering: a chat that recovered after failed
  // "Opnieuw" attempts hydrates each dead retry as its own assistant row, so
  // reopening it would draw one "search failed" box per dead turn. Keep only
  // the last of each consecutive assistant run (FUN-4558).
  const visibleMessages = collapseSupersededAssistantTurns(uiMessages).filter(shouldRenderChatMessage);
  const hasMessages = visibleMessages.length > 0;
  // Continuous loading indicator: keep the "Thinking…" shimmer up across the
  // whole in-flight turn EXCEPT while a tool is actively running (the in-body
  // "Searching activities…" spinner owns that phase) or once visible output has
  // arrived. This bridges the submit→search gap and the between-retry gaps.
  const lastMessage = visibleMessages[visibleMessages.length - 1];
  const showThinking = isStreaming && (() => {
    if (lastMessage?.role !== 'assistant') return true;
    const searchIndex = getLastSearchToolPartIndex(lastMessage);
    const searchPart = searchIndex >= 0 ? lastMessage.parts[searchIndex] : undefined;
    const isSearching = searchPart !== undefined && isToolUIPart(searchPart) && (searchPart.state === 'input-streaming' || searchPart.state === 'input-available');
    const hasVisibleOutput = searchPart !== undefined && isToolUIPart(searchPart) && searchPart.state === 'output-available' || lastMessage.parts.some(p => p.type === 'text' && p.text.length > 0);
    return !isSearching && !hasVisibleOutput;
  })();
  const showLongConversationNotice = shouldShowLongConversationNotice(uiMessages.length);
  const canSend = draft.trim().length > 0;

  // Callback ref: fires exactly when the node attaches. Replaces the prior
  // `useRef + useEffect + requestAnimationFrame` autofocus dance — no
  // ambient effect, no manual cleanup. Still writes to `textareaRef.current`
  // so existing imperative refocus calls (after submit / send) keep working.
  const setTextareaRef = useCallback((node: HTMLTextAreaElement | null) => {
    const wasNew = textareaRef.current !== node;
    textareaRef.current = node;
    if (!autoFocusOnMount || !node || !wasNew) return;
    if (document.activeElement === node) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (document.activeElement instanceof HTMLTextAreaElement) return;
    node.focus({
      preventScroll: true
    });
  }, [autoFocusOnMount]);

  // Auto-grow the composer so the user always sees everything they type,
  // up to a cap (then it scrolls). Keyed on `draft` so it handles both typing
  // AND programmatic changes (submit clears → shrinks; edit/reply prefill →
  // grows). `height: auto` first lets the textarea shrink back when lines are
  // removed before we measure `scrollHeight`.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [draft]);
  const [, setUrlAiState] = useQueryStates({
    query: parseAsString
  }, {
    shallow: true,
    scroll: false,
    clearOnDefault: true
  });

  // "Go back" to a past search result: clicking its summary re-applies that
  // turn's id set to the grid. Defaults to the latest turn (driven by
  // `ActivityAiResultsBridge`); resets whenever a new turn arrives.
  const applyAiResult = useApplyAiResult();
  const [appliedResultMessageId, setAppliedResultMessageId] = useState<string | null>(null);
  const latestMessageId = uiMessages[uiMessages.length - 1]?.id ?? null;

  /**
   * All assistant turns that produced a `search_activities` result, in order.
   * Drives the prev/next "result history" nav above the composer so users can
   * step through past results without scrolling up to click an old summary.
   */
  const searchResults = useMemo(() => {
    const list: {
      messageId: string;
      filters: SearchActivitiesFilters;
      activityIds: number[];
    }[] = [];
    for (const msg of uiMessages) {
      if (msg.role !== 'assistant') continue;
      for (const part of msg.parts) {
        if (!isToolUIPart(part)) continue;
        if (getToolName(part) !== 'search_activities') continue;
        if (part.state !== 'output-available') continue;
        const parsed = SearchActivitiesOutputSchema.safeParse('output' in part ? part.output : undefined);
        if (!parsed.success) continue;
        list.push({
          messageId: msg.id,
          filters: parsed.data.filters,
          activityIds: parsed.data.activityIds
        });
        break; // one result per assistant message
      }
    }
    return list;
  }, [uiMessages]);

  /**
   * Per-assistant-turn derivations that used to be computed inside
   * `AssistantMessageBody` from the full `allMessages` array. Lifting them here
   * (a) keeps the memoized child from receiving the whole `uiMessages` array —
   * whose reference changes on every streaming token — and (b) yields values
   * that are referentially stable for OLDER turns (only the last turn's
   * turn changes), so `React.memo` skips re-rendering + re-parsing every prior
   * turn's markdown while a new answer streams in.
   *
   * The value is the metadata of the nearest preceding user message (the filter
   * snapshot recorded on the turn that produced this result).
   */
  const assistantTurnMetaById = useMemo(() => {
    const map = new Map<string, {
      appliedFilterChips?: unknown;
    } | undefined>();
    for (let index = 0; index < uiMessages.length; index++) {
      const message = uiMessages[index];
      if (message?.role !== 'assistant') continue;
      let turnMeta: {
        appliedFilterChips?: unknown;
      } | undefined;
      for (let i = index - 1; i >= 0; i--) {
        const previous = uiMessages[i];
        if (previous?.role !== 'user') continue;
        turnMeta = previous.metadata as {
          appliedFilterChips?: unknown;
        } | undefined;
        break;
      }
      map.set(message.id, turnMeta);
    }
    return map;
  }, [uiMessages]);
  const latestSearchResultId = searchResults.length > 0 ? searchResults[searchResults.length - 1]!.messageId : null;
  const activeSearchResultId = appliedResultMessageId ?? latestSearchResultId;
  // Look up unconditionally: a persisted/hydrated message can have an EMPTY id (""),
  // which is falsy — a `activeSearchResultId ? … : -1` guard would wrongly return -1 for
  // the first result and make the prev/next switcher vanish. `findIndex` returns -1 on
  // its own when there's genuinely no match (null id, fresh draft).
  const activeSearchIndex = searchResults.findIndex(r => r.messageId === activeSearchResultId);
  const goToSearchResult = (index_0: number) => {
    const target = searchResults[index_0];
    if (!target) return;
    // Set `null` when stepping to the latest result so the grid's derived
    // selected-turn stays in its "auto-apply latest" default; otherwise pin
    // to the chosen id (also written to the URL so the grid reads it back).
    const pinnedMessageId = index_0 === searchResults.length - 1 ? null : target.messageId;
    setAppliedResultMessageId(pinnedMessageId);
    applyAiResult(target.filters, target.activityIds, pinnedMessageId);
    // Scroll the chat to the turn that produced this result so the user sees
    // the matching conversation, not just the grid swap. We set the chat
    // scroller's `scrollTop` directly instead of `el.scrollIntoView` — the
    // latter walks up and scrolls EVERY scrollable ancestor (incl. the page),
    // which yanked the grid. This moves ONLY the chat container. rAF lets the
    // apply settle first; navigating doesn't touch `uiMessages`/`status`, so
    // the auto-scroll-to-bottom effect won't fight it.
    requestAnimationFrame(() => {
      const scroller = scrollerRef.current;
      const el_0 = scroller?.querySelector(`[data-ai-message-id="${target.messageId}"]`);
      if (!scroller || !el_0) return;
      const top = el_0.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      scroller.scrollTo({
        top: top - 8,
        behavior: 'smooth'
      });
    });
  };
  // Reset the "active result" pointer the moment a new turn arrives — derive
  // during render via previous-value tracking (guide: "Adjusting State on
  // Prop Change") instead of a `useEffect([latestMessageId])`.
  const [prevLatestMessageId, setPrevLatestMessageId] = useState<string | null>(latestMessageId);
  if (prevLatestMessageId !== latestMessageId) {
    setPrevLatestMessageId(latestMessageId);
    setAppliedResultMessageId(null);
  }

  // Seed the grid URL from the latest resolved result so a fresh turn's extracted
  // participants/budget/categories reach the grid + drawer (else cards price for
  // the 20-person fallback and the "filters changed" note fires spuriously).
  useAutoApplyAiResult({
    chatId,
    latestResult: searchResults[searchResults.length - 1] ?? null,
    appliedResultMessageId,
    applyAiResult
  });

  // The answer lands on the results grid behind the sheet, which an 85dvh
  // drawer would cover. No-op on desktop, which has no drawer.
  const closeDrawerOnSend = () => {
    if (variant === 'mobile') onCloseDrawer?.();
  };
  const submitNewChat = (text: string) => {
    const trimmed = text.trim();
    if (trimmed.length === 0 || isStreaming) return;
    setEditingMessageId(null);
    setReplyingTo(null);
    closeDrawerOnSend();
    void setUrlAiState({
      query: null
    });
    // The grid shows its loading skeleton for the in-flight window automatically:
    // `isAiLoading` (streaming + empty id set) covers it until the new result's ids
    // land, so the grid never falls back to the full unfiltered search.
    startNewChat(trimmed);
    setDraft('');
    requestAnimationFrame(() => textareaRef.current?.focus({
      preventScroll: true
    }));
  };
  const dispatchPrompt = (text_0: string) => {
    if (editingMessageId) {
      editAndResend(editingMessageId, text_0);
      setEditingMessageId(null);
    } else if (replyingTo) {
      replyToMessage(replyingTo.id, text_0);
      setReplyingTo(null);
    } else {
      // Snapshot the currently-applied filters onto the user message (faithful,
      // persisted result history) AND pass the tool-mappable subset as `activeFilters`
      // so the model keeps the drawer-edited filters on this turn's search.
      const fv = searchFilters.value;
      sendMessage(text_0, {
        appliedFilterChips: appliedFilterChips.map(c => ({
          id: c.id,
          label: c.label,
          section: DRAWER_SECTION_BY_CHIP_ID[c.id]
        }))
      }, {
        participants: fv.participants ?? undefined,
        minBudget: fv.minBudget ?? undefined,
        maxBudget: fv.maxBudget ?? undefined,
        locationType: fv.locationType?.length ? fv.locationType : undefined,
        season: fv.season?.length ? fv.season : undefined,
        cleanliness: fv.cleanliness?.length ? fv.cleanliness : undefined
      });
    }
  };

  // Pending prompts the user typed while a stream was still in flight. The
  // queue + flush-on-idle effect lives in the hook (the guide's accepted
  // escape hatch for raw effects) so the panel only sees a clean API.
  const pendingQueue = usePendingPromptQueue({
    isStreaming,
    dispatch: dispatchPrompt
  });
  const queuedCount = pendingQueue.queue.length;
  const submit = (text_1: string) => {
    const trimmed_0 = text_1.trim();
    if (trimmed_0.length === 0) return;
    if (isStreaming) {
      pendingQueue.enqueue(trimmed_0);
    } else {
      dispatchPrompt(trimmed_0);
    }
    closeDrawerOnSend();
    setDraft('');
    requestAnimationFrame(() => textareaRef.current?.focus({
      preventScroll: true
    }));
  };

  // Grid → chat bridge: the results grid's "show 10 more relevant" footer
  // action pushes a follow-up into THIS thread via the followup store. Route
  // it through `submit` so it queues if a stream is still in flight.
  useFollowupAutoSend(submit);
  const forceSendNow = () => {
    if (isStreaming) stop();
    // Drain returns the current queue and resets it; we prepend any draft
    // and dispatch the head immediately. Remaining items get re-queued so
    // the hook's idle-flush effect can drain them in order.
    const trimmedDraft = draft.trim();
    const drained = pendingQueue.drain();
    const sequence = [...(trimmedDraft ? [trimmedDraft] : []), ...drained];
    if (sequence.length === 0) return;
    setDraft('');
    const [first, ...rest] = sequence;
    if (first) dispatchPrompt(first);
    for (const t of rest) pendingQueue.enqueue(t);
    requestAnimationFrame(() => textareaRef.current?.focus({
      preventScroll: true
    }));
  };
  const startReply = (message_0: UIMessage) => {
    if (isStreaming) return;
    // Replying and editing are mutually exclusive — picking reply cancels
    // any in-progress edit so the composer's submit branch is unambiguous.
    setEditingMessageId(null);
    setReplyingTo(extractQuotePreview(message_0));
    requestAnimationFrame(() => textareaRef.current?.focus({
      preventScroll: true
    }));
  };
  const cancelReply = () => setReplyingTo(null);
  const startEdit = (messageId: string, text_2: string) => {
    if (isStreaming) return;
    setReplyingTo(null);
    setEditingMessageId(messageId);
    setDraft(text_2);
    // Focus + place cursor at end so the user can refine the prompt.
    requestAnimationFrame(() => {
      const el_1 = textareaRef.current;
      if (!el_1) return;
      el_1.focus({
        preventScroll: true
      });
      const len = el_1.value.length;
      el_1.setSelectionRange(len, len);
    });
  };
  const cancelEdit = () => {
    setEditingMessageId(null);
    setDraft('');
  };

  // Stable per-row handlers for the memoized `AssistantMessageBody`. Several of
  // the underlying closures (`submit`, `startReply`, `regenerate`, `applyAiResult`)
  // are recreated every render, so the map's inline arrows used to be a fresh
  // reference on every streaming token — invalidating React.memo on EVERY prior
  // assistant turn and re-parsing its markdown. The latest closures live in a ref
  // updated after each commit and are read through dispatchers that are stable for
  // the component's lifetime (`useCallback([])`).
  const rowHandlersRef = useLatest({
    submit,
    startReply,
    regenerate,
    applyAiResult
  });
  const handleRowReply = useCallback((message_1: UIMessage) => rowHandlersRef.current.startReply(message_1), []);
  const handleRowRetry = useCallback((messageId_0: string) => rowHandlersRef.current.regenerate({
    messageId: messageId_0
  }), []);
  const handleRowApplyResult = useCallback((messageId_1: string, filters: SearchActivitiesFilters, activityIds: number[]) => {
    setAppliedResultMessageId(messageId_1);
    rowHandlersRef.current.applyAiResult(filters, activityIds, messageId_1);
  }, []);
  return <div className={cn('relative flex min-h-0 flex-1 flex-col', className)} data-testid="ai-chat-panel">
            <div className="relative flex min-h-0 flex-1 flex-col">
                <ActivityAiHistoryDrawer open={historyVisible} activeThreadId={chatId} onClose={closeHistory} onSelectThread={id => openThread(id)} onCloseSheet={onCloseDrawer} hideHeader={historyHeaderInParent} />
                <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3 [scrollbar-gutter:stable]" data-testid="ai-chat-messages">
                    {hasMessages ? <ul className="flex flex-col gap-2" data-testid="ai-chat-message-list">
                            {visibleMessages.map((msg_0, idx) => <li key={msg_0.id} data-ai-message-id={msg_0.id}
          // `scroll-mt-2` gives a little breathing room when the
          // result-history nav scrolls this turn to the top.
          className={cn('flex scroll-mt-2', msg_0.role === 'user' ? 'justify-end' : 'justify-start')}>
                                    {msg_0.role === 'assistant' ? <div className="group flex max-w-[90%] items-start gap-2">
                                            <div className="flex min-w-0 flex-1 flex-col gap-2">
                                                <AssistantMessageBody message={msg_0} variant={variant} onCloseDrawer={onCloseDrawer} isLatest={idx === visibleMessages.length - 1} assistantTextIsAnimating={idx === visibleMessages.length - 1 && status === 'streaming'} canReply={!isStreaming} onReply={handleRowReply} onRetry={handleRowRetry} turnMeta={assistantTurnMetaById.get(msg_0.id)} isAdmin={isAdmin} isActiveResult={(appliedResultMessageId ?? latestMessageId) === msg_0.id} onApplyResult={handleRowApplyResult} chatId={chatId} />
                                            </div>
                                        </div> : msg_0.role === 'user' ? <UserMessageBubble text={collectUserText(msg_0)} isEditing={editingMessageId === msg_0.id} canEdit={!isStreaming} onEdit={() => startEdit(msg_0.id, collectUserText(msg_0))} onReply={() => startReply(msg_0)} /> : null}
                                </li>)}
                            {showThinking ? <li className="flex justify-start" data-testid="ai-chat-thinking">
                                    <ChatShimmerLoader label={i18n._({
              id: 'activity.search.ai.thinking',
              message: 'Thinking…'
            })} />
                                </li> : null}
                            {error && !isStreaming ? <li className="flex justify-start" data-testid="ai-chat-error">
                                    <div className="flex max-w-[90%] flex-col gap-2 rounded-3xl rounded-tl-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
                                        <span className="font-medium">
                                            {isRateLimitError(error) ? i18n._({
                  id: 'activity.search.ai.error.rateLimit',
                  message: 'Rate limit reached. Try again in a moment or switch model.'
                }) : i18n._({
                  id: 'activity.search.ai.error.generic',
                  message: 'Something went wrong. Please try again.'
                })}
                                        </span>
                                        {rateLimitInfo ? <RateLimitDetail info={rateLimitInfo} /> : <span className="font-mono text-[10px] break-words text-red-900/60">
                                                {extractErrorMessage(error)}
                                            </span>}
                                        <button type="button" onClick={() => regenerate()} data-testid="ai-chat-error-retry" className="self-start rounded-full bg-red-900 px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-red-900/90">
                                            {i18n._({
                  id: 'activity.search.ai.error.retry',
                  message: 'Retry'
                })}
                                        </button>
                                    </div>
                                </li> : null}
                        </ul> : isHydrating ? <div className="flex items-center justify-center gap-2 py-10 text-sm text-blue-900/50" data-testid="ai-chat-hydrating">
                            <Loader2 className="size-4 animate-spin" aria-hidden />
                            {i18n._('activity.search.ai.loadingChat', undefined, {
            message: 'Loading conversation…'
          })}
                        </div> : chatId ?
        // A thread IS open but its transcript came back empty (transient miss,
        // or not persisted) — show a recoverable state, NOT the all-chats
        // landing (which would confusingly list "recent chats" inside a thread).
        <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="ai-chat-load-failed">
                            <p className="text-sm text-blue-900/60">
                                {i18n._('activity.search.ai.loadFailed', undefined, {
              message: 'This conversation could not be loaded.'
            })}
                            </p>
                            <button type="button" onClick={() => reloadMessages()} className="inline-flex items-center gap-1.5 rounded-full border border-blue-900/10 bg-white px-3 py-1 text-xs font-medium text-blue-900 transition-colors hover:border-secondary/40 hover:bg-secondary/5">
                                <RotateCcw className="size-3.5" strokeWidth={2.25} aria-hidden />
                                {i18n._('activity.search.ai.retry', undefined, {
              message: 'Try again'
            })}
                            </button>
                        </div> : <div className="flex flex-col gap-6" data-testid="ai-chat-empty-state">
                            <p className="text-sm leading-relaxed text-blue-600/90">
                                {i18n._('activity.search.ai.sidebarHint', undefined, {
              message: 'Describe your team and I’ll line up the best teambuildings on the right.'
            })}
                            </p>

                            {sortedThreads.length > 0 ? <div className="flex flex-col gap-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold text-blue-900/70">
                                            {i18n._('activity.search.ai.recentChats', undefined, {
                  message: 'Recent chats'
                })}
                                        </p>
                                        <button type="button" onClick={openHistory} aria-label={i18n._('activity.search.ai.historyTrigger', undefined, {
                message: 'Previous chats'
              })} className="rounded-full p-1 text-blue-900/40 transition-colors hover:bg-blue-50 hover:text-blue-900">
                                            <History className="size-3.5" strokeWidth={2.25} aria-hidden />
                                        </button>
                                    </div>
                                    <RecentChatsList threads={sortedThreads} onSelect={id_0 => {
              analytics[AI_SEARCH_EVENTS.CHAT_RESUMED]({
                session_id: id_0,
                source: 'recent_list'
              });
              openThread(id_0);
            }} showSearch variant="sidebar" />
                                </div> : null}
                        </div>}
                    {showLongConversationNotice ? <div className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl border border-blue-900/10 bg-blue-50/60 px-3 py-2 text-xs text-blue-900">
                            <MessageCircleMore className="size-4 shrink-0 text-secondary" strokeWidth={2.25} aria-hidden />
                            <span className="min-w-0 flex-1 leading-snug">
                                {i18n._('search.ai.contextLimit.notice', undefined, {
              message: 'This conversation is getting long — want to start a fresh search?'
            })}
                            </span>
                            <button type="button" onClick={() => goToAllChats()} className="shrink-0 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-secondary/90">
                                {i18n._('search.ai.contextLimit.cta', undefined, {
              message: 'New search'
            })}
                            </button>
                        </div> : null}
                </div>
            </div>

            {/* Hide nav while streaming: the in-flight turn's result isn't in
                `searchResults` yet, so pressing the buttons would apply an
                older result and feel like going the wrong way. */}
            {(searchResults.length >= 2 && activeSearchIndex >= 0 || isAdmin) && !isStreaming ? <div data-testid="ai-chat-result-history-nav" className="shrink-0 px-4 pt-2">
                    <div className="flex items-center gap-1.5 text-[11px] text-blue-900/60">
                        {searchResults.length >= 2 && activeSearchIndex >= 0 ? <>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button type="button" onClick={() => goToSearchResult(activeSearchIndex - 1)} disabled={activeSearchIndex <= 0} data-testid="ai-chat-result-history-prev" aria-label={i18n._({
                id: 'activity.search.ai.resultHistory.prev',
                message: 'Previous result'
              })} className="inline-flex size-7 items-center justify-center rounded-full text-blue-900/70 transition-colors hover:bg-blue-50 hover:text-blue-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent">
                                            <ChevronLeft className="size-4" strokeWidth={2.25} aria-hidden />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {i18n._({
                id: 'activity.search.ai.resultHistory.prev',
                message: 'Previous result'
              })}
                                    </TooltipContent>
                                </Tooltip>
                                <span className="font-mono tabular-nums" aria-live="polite">
                                    {activeSearchIndex + 1}/{searchResults.length}
                                </span>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button type="button" onClick={() => goToSearchResult(activeSearchIndex + 1)} disabled={activeSearchIndex >= searchResults.length - 1} data-testid="ai-chat-result-history-next" aria-label={i18n._({
                id: 'activity.search.ai.resultHistory.next',
                message: 'Next result'
              })} className="inline-flex size-7 items-center justify-center rounded-full text-blue-900/70 transition-colors hover:bg-blue-50 hover:text-blue-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent">
                                            <ChevronRight className="size-4" strokeWidth={2.25} aria-hidden />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {i18n._({
                id: 'activity.search.ai.resultHistory.next',
                message: 'Next result'
              })}
                                    </TooltipContent>
                                </Tooltip>
                            </> : null}
                        {isAdmin ? <div className="ml-auto flex items-center gap-2">
                                <ChatSpendingBadge messages={uiMessages} />
                            </div> : null}
                    </div>
                </div> : null}

            <form data-testid="ai-chat-form" className="shrink-0 border-t border-blue-900/10 px-4 pt-3 pb-4" onSubmit={e => {
      e.preventDefault();
      if (!canSend) return;
      submit(draft);
    }}>
                {replyingTo ? <div data-testid="ai-chat-reply-banner" className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-blue-50 px-3 py-1.5 text-xs text-blue-900">
                        <span className="inline-flex min-w-0 items-center gap-1.5">
                            <CornerDownRight className="size-3 shrink-0" strokeWidth={2.25} aria-hidden />
                            <span className="min-w-0 truncate">
                                {i18n._('activity.search.ai.quotedContext', undefined, {
              message: 'Replying to:'
            })}
                                {replyingTo.excerpt.length > 0 ? ` ${replyingTo.excerpt}` : ''}
                            </span>
                        </span>
                        <button type="button" onClick={cancelReply} aria-label={i18n._('activity.search.ai.removeQuote', undefined, {
          message: 'Remove quoted message'
        })} className="rounded-full p-0.5 text-blue-900/60 transition-colors hover:bg-blue-100 hover:text-blue-900">
                            <X className="size-3.5" strokeWidth={2.25} aria-hidden />
                        </button>
                    </div> : null}
                {editingMessageId ? <div data-testid="ai-chat-edit-banner" className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
                        <span className="inline-flex items-center gap-1.5">
                            <Pencil className="size-3" strokeWidth={2.25} aria-hidden />
                            {i18n._('activity.search.ai.editingPromptHint', undefined, {
            message: 'Editing a previous message — sending replaces everything after it.'
          })}
                        </span>
                        <button type="button" onClick={cancelEdit} aria-label={i18n._('Cancel')} className="rounded-full p-0.5 text-amber-900/70 transition-colors hover:bg-amber-100 hover:text-amber-900">
                            <X className="size-3.5" strokeWidth={2.25} aria-hidden />
                        </button>
                    </div> : null}
                {queuedCount > 0 ? <div data-testid="ai-chat-queue-banner" className="mb-2 flex flex-col gap-1.5 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900">
                        <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">
                                {i18n._('activity.search.ai.queuedTitle', {
              count: queuedCount
            }, {
              message: '{count, plural, one {# message queued} other {# messages queued}}'
            })}
                            </span>
                            <div className="flex items-center gap-1">
                                <button type="button" onClick={forceSendNow} className="rounded-full bg-amber-900 px-2 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-amber-900/90">
                                    {i18n._({
                id: 'activity.search.ai.forceSendNow',
                message: 'Stop and send now'
              })}
                                </button>
                                <button type="button" onClick={pendingQueue.clear} aria-label={i18n._({
              id: 'activity.search.ai.clearQueue',
              message: 'Clear queue'
            })} className="rounded-full p-0.5 text-amber-900/70 transition-colors hover:bg-amber-100 hover:text-amber-900">
                                    <X className="size-3.5" strokeWidth={2.25} aria-hidden />
                                </button>
                            </div>
                        </div>
                        <ul className="flex flex-col gap-1">
                            {pendingQueue.queue.map((msg_1, idx_0) => <li key={`${idx_0}-${msg_1}`} className="flex items-start justify-between gap-2 rounded-md bg-white/70 px-2 py-1">
                                    <span className="flex-1 leading-snug wrap-break-word">
                                        {msg_1}
                                    </span>
                                    <button type="button" onClick={() => pendingQueue.removeAt(idx_0)} aria-label={i18n._({
              id: 'activity.search.ai.removeFromQueue',
              message: 'Remove from queue'
            })} className="shrink-0 rounded-full p-0.5 text-amber-900/60 transition-colors hover:bg-amber-100 hover:text-amber-900">
                                        <X className="size-3" strokeWidth={2.25} aria-hidden />
                                    </button>
                                </li>)}
                        </ul>
                    </div> : null}
                <div className="flex flex-col gap-1.5 rounded-2xl bg-transparent px-0 py-1">
                    <label htmlFor="activity-ai-chat-followup" className="sr-only">
                        {i18n._('activity.search.ai.followupLabel', undefined, {
            message: 'Follow up'
          })}
                    </label>
                    <textarea ref={setTextareaRef} id="activity-ai-chat-followup" data-testid="ai-chat-input" value={draft} onChange={e_0 => setDraft(e_0.target.value)} onKeyDown={e_1 => {
          if (e_1.key === 'Enter' && !e_1.shiftKey) {
            e_1.preventDefault();
            if (!canSend) return;
            submit(draft);
          }
          if (e_1.key === 'Escape' && editingMessageId) {
            e_1.preventDefault();
            cancelEdit();
          }
          if (e_1.key === 'Escape' && replyingTo) {
            e_1.preventDefault();
            cancelReply();
          }
        }} rows={1} placeholder={hasMessages ? i18n._({
          id: 'activity.search.ai.followupPlaceholder',
          message: 'Ask a follow-up…'
        }) : i18n._({
          id: 'activity.search.ai.firstPromptPlaceholder',
          message: 'Describe your team…'
        })}
        // `hide-scrollbar`: still scrolls past its max height, but the
        // track is never painted — it showed on an empty composer
        // wherever the platform paints scrollbars permanently.
        className="hide-scrollbar max-h-[200px] min-h-[1.5rem] w-full resize-none overflow-y-auto border-0 bg-transparent px-0 py-1 text-sm leading-snug text-stone-800 ring-0 ring-offset-0 outline-none placeholder:text-stone-400 focus-visible:ring-0 focus-visible:outline-none disabled:opacity-60" />
                    <div className="flex items-center justify-end gap-2">
                        <div className="flex shrink-0 items-center gap-1.5">
                            {queuedCount > 0 ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900" title={i18n._('activity.search.ai.queuedCount', {
              count: queuedCount
            }, {
              message: '{count, plural, one {# queued} other {# queued}}'
            })}>
                                    {i18n._('activity.search.ai.queuedCount', {
                count: queuedCount
              }, {
                message: '{count, plural, one {# queued} other {# queued}}'
              })}
                                </span> : null}
                            {isStreaming ? <>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button type="button" onClick={forceSendNow} disabled={!canSend && queuedCount === 0} aria-label={i18n._({
                    id: 'activity.search.ai.forceSendNow',
                    message: 'Stop and send now'
                  })} className={cn('inline-flex size-8 items-center justify-center rounded-full border transition-colors', canSend || queuedCount > 0 ? 'border-blue-900/15 bg-white text-blue-900 hover:border-secondary/40 hover:bg-secondary/5' : 'cursor-not-allowed border-stone-200 bg-stone-100 text-stone-400')}>
                                                <ChevronRight className="size-4" strokeWidth={2.25} aria-hidden />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            {i18n._({
                    id: 'activity.search.ai.forceSendNow',
                    message: 'Stop and send now'
                  })}
                                        </TooltipContent>
                                    </Tooltip>
                                    <button type="button" onClick={() => stop()} data-testid="ai-chat-stop" aria-label={i18n._({
                id: 'activity.search.ai.stop',
                message: 'Stop'
              })} className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-900 text-white transition-colors hover:bg-blue-900/90">
                                        <Square className="size-3.5" strokeWidth={2.5} aria-hidden />
                                    </button>
                                </> : <>
                                    {canSend && hasMessages ? <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button type="button" onClick={() => submitNewChat(draft)} aria-label={i18n._({
                    id: 'activity.search.ai.submitNewChat',
                    message: 'Send and start a new chat'
                  })} className="inline-flex size-8 items-center justify-center rounded-full border border-blue-900/15 bg-white text-blue-900 transition-colors hover:border-secondary/40 hover:bg-secondary/5">
                                                    <ChevronRight className="size-4" strokeWidth={2.25} aria-hidden />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                                {i18n._({
                    id: 'activity.search.ai.submitNewChat',
                    message: 'Send and start a new chat'
                  })}
                                            </TooltipContent>
                                        </Tooltip> : null}
                                    <button type="submit" disabled={!canSend} data-testid="ai-chat-submit" aria-label={i18n._({
                id: 'activity.search.ai.followupSubmit',
                message: 'Send'
              })} className={cn('inline-flex size-8 items-center justify-center rounded-full transition-colors', canSend ? 'bg-secondary text-white hover:bg-secondary/90' : 'cursor-not-allowed bg-stone-200 text-stone-400')}>
                                        <ArrowUp className="size-4" strokeWidth={2.25} aria-hidden />
                                    </button>
                                </>}
                        </div>
                    </div>
                </div>
            </form>
        </div>;
};

/**
 * "Was this a good answer? Yes / No". Local state only — the verdict is an
 * analytics signal, not persisted, so a reopened thread asks again.
 */
const AssistantAnswerFeedback: React.FC<{
  chatId: string;
  messageId: string;
  traceId?: string;
}> = t0 => {
  const $ = _c(33);
  const {
    chatId,
    messageId,
    traceId
  } = t0;
  const i18n = useI18n();
  const posthog = usePostHog();
  const [rating, setRating] = useState(null);
  let t1;
  if ($[0] !== chatId || $[1] !== messageId || $[2] !== posthog || $[3] !== traceId) {
    t1 = value => {
      setRating(value);
      captureAnswerRating(posthog, {
        chatId,
        messageId,
        traceId,
        rating: value
      });
    };
    $[0] = chatId;
    $[1] = messageId;
    $[2] = posthog;
    $[3] = traceId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const rate = t1;
  if (rating) {
    let t2;
    if ($[5] !== i18n) {
      t2 = i18n._("activity.search.ai.feedback.thanks", undefined, {
        message: "Thanks for your feedback!"
      });
      $[5] = i18n;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== t2) {
      t3 = <p className="text-[11px] text-blue-900/50" data-testid="ai-chat-answer-feedback-thanks">{t2}</p>;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  let t2;
  if ($[9] !== i18n) {
    t2 = i18n._("activity.search.ai.feedback.question", undefined, {
      message: "Was this a good answer?"
    });
    $[9] = i18n;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t2) {
    t3 = <span>{t2}</span>;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== rate) {
    t4 = () => rate("good");
    $[13] = rate;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ThumbsUp className="size-3" strokeWidth={2.25} aria-hidden={true} />;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== i18n) {
    t6 = i18n._("activity.search.ai.feedback.yes", undefined, {
      message: "Yes"
    });
    $[16] = i18n;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <button type="button" onClick={t4} data-testid="ai-chat-answer-feedback-good" className="inline-flex items-center gap-1 rounded-full border border-blue-900/10 bg-white px-2.5 py-0.5 text-blue-900/80 transition-colors hover:border-secondary/40 hover:bg-secondary/5">{t5}{t6}</button>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== rate) {
    t8 = () => rate("bad");
    $[21] = rate;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ThumbsDown className="size-3" strokeWidth={2.25} aria-hidden={true} />;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== i18n) {
    t10 = i18n._("activity.search.ai.feedback.no", undefined, {
      message: "No"
    });
    $[24] = i18n;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t8) {
    t11 = <button type="button" onClick={t8} data-testid="ai-chat-answer-feedback-bad" className="inline-flex items-center gap-1 rounded-full border border-blue-900/10 bg-white px-2.5 py-0.5 text-blue-900/80 transition-colors hover:border-secondary/40 hover:bg-secondary/5">{t9}{t10}</button>;
    $[26] = t10;
    $[27] = t8;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11 || $[30] !== t3 || $[31] !== t7) {
    t12 = <div className="flex flex-wrap items-center gap-2 text-[11px] text-blue-900/60" data-testid="ai-chat-answer-feedback">{t3}{t7}{t11}</div>;
    $[29] = t11;
    $[30] = t3;
    $[31] = t7;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
};
const UserMessageBubble: React.FC<{
  text: string;
  isEditing: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onReply: () => void;
}> = t0 => {
  const $ = _c(13);
  const {
    text,
    isEditing,
    canEdit,
    onEdit,
    onReply
  } = t0;
  const i18n = useI18n();
  let t1;
  if ($[0] !== canEdit || $[1] !== i18n || $[2] !== onEdit || $[3] !== onReply) {
    t1 = canEdit ? <div className="mt-1 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100"><button type="button" onClick={onReply} data-testid="ai-chat-user-reply" aria-label={i18n._("activity.search.ai.replyToMessage", undefined, {
        message: "Reply to this message"
      })} className="rounded-full p-1 text-blue-900/40 transition-colors hover:bg-blue-50 hover:text-blue-900"><CornerDownRight className="size-3.5" strokeWidth={2.25} aria-hidden={true} /></button><button type="button" onClick={onEdit} data-testid="ai-chat-user-edit" aria-label={i18n._("activity.search.ai.editPrompt", undefined, {
        message: "Edit this message"
      })} className="rounded-full p-1 text-blue-900/40 transition-colors hover:bg-blue-50 hover:text-blue-900"><Pencil className="size-3.5" strokeWidth={2.25} aria-hidden={true} /></button></div> : null;
    $[0] = canEdit;
    $[1] = i18n;
    $[2] = onEdit;
    $[3] = onReply;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = isEditing ? "bg-amber-100 text-blue-900 ring-2 ring-amber-300" : "bg-blue-900 text-white";
  let t3;
  if ($[5] !== t2) {
    t3 = cn("rounded-3xl rounded-tr-md px-5 py-3.5 text-sm leading-relaxed", t2);
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3 || $[8] !== text) {
    t4 = <div data-testid="ai-chat-user-bubble" className={t3}>{text}</div>;
    $[7] = t3;
    $[8] = text;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t4) {
    t5 = <div className="group flex max-w-[85%] items-start gap-1.5" data-testid="ai-chat-user-message">{t1}{t4}</div>;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
const AssistantMessageBody = memo(function AssistantMessageBody(t0) {
  const $ = _c(50);
  const {
    message,
    variant,
    onCloseDrawer,
    isLatest,
    assistantTextIsAnimating,
    canReply,
    onReply,
    onRetry,
    turnMeta,
    isAdmin,
    isActiveResult,
    onApplyResult,
    chatId
  } = t0;
  const i18n = useI18n();
  const storedFilterChips = Array.isArray(turnMeta?.appliedFilterChips) ? turnMeta.appliedFilterChips as {
    id: string;
    label: string;
    section?: string;
  }[] : undefined;
  let lastTextPartIndex;
  if ($[0] !== message.parts) {
    lastTextPartIndex = -1;
    for (let i = 0; i < message.parts.length; i++) {
      if (message.parts[i]?.type === "text") {
        lastTextPartIndex = i;
      }
    }
    $[0] = message.parts;
    $[1] = lastTextPartIndex;
  } else {
    lastTextPartIndex = $[1];
  }
  let t1;
  if ($[2] !== message) {
    t1 = getLastSearchToolPartIndex(message);
    $[2] = message;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const lastSearchToolPartIndex = t1;
  const hasAnswer = lastTextPartIndex >= 0 && !assistantTextIsAnimating;
  let t2;
  if ($[4] !== assistantTextIsAnimating || $[5] !== canReply || $[6] !== i18n || $[7] !== isActiveResult || $[8] !== isAdmin || $[9] !== isLatest || $[10] !== lastSearchToolPartIndex || $[11] !== lastTextPartIndex || $[12] !== message.id || $[13] !== message.parts || $[14] !== onApplyResult || $[15] !== onCloseDrawer || $[16] !== onRetry || $[17] !== storedFilterChips || $[18] !== variant) {
    let t3;
    if ($[20] !== assistantTextIsAnimating || $[21] !== canReply || $[22] !== i18n || $[23] !== isActiveResult || $[24] !== isAdmin || $[25] !== isLatest || $[26] !== lastSearchToolPartIndex || $[27] !== lastTextPartIndex || $[28] !== message.id || $[29] !== onApplyResult || $[30] !== onCloseDrawer || $[31] !== onRetry || $[32] !== storedFilterChips || $[33] !== variant) {
      t3 = (part, idx) => {
        if (part.type === "reasoning") {
          if (!isAdmin) {
            return null;
          }
          const reasoningText = (part as {
            text?: string;
          }).text ?? "";
          if (reasoningText.length === 0) {
            return null;
          }
          const partState = (part as {
            state?: 'streaming' | 'done';
          }).state;
          const reasoningIsStreaming = partState !== undefined ? partState === "streaming" : assistantTextIsAnimating;
          return <ReasoningBlock key={`${message.id}-reasoning-${idx}`} text={reasoningText} isStreaming={reasoningIsStreaming} />;
        }
        if (part.type === "text") {
          if (idx !== lastTextPartIndex) {
            return null;
          }
          return <div key={`${message.id}-text-${idx}`} data-testid="ai-chat-assistant-message" className="prose prose-sm max-w-none min-w-0 rounded-3xl rounded-tl-md bg-stone-100 px-5 py-3.5 text-sm leading-relaxed [overflow-wrap:anywhere] text-blue-900 prose-headings:my-1 prose-p:my-0.5 prose-a:break-all prose-ol:my-1 prose-ul:my-1 prose-li:my-0"><Streamdown mode="streaming" parseIncompleteMarkdown={true} plugins={activityAiStreamdownPlugins} animated={activityAiStreamdownAnimate} isAnimating={assistantTextIsAnimating} linkSafety={activityAiLinkSafety}>{sanitizeAssistantText(part.text)}</Streamdown></div>;
        }
        if (isToolUIPart(part) && getToolName(part) === "search_activities") {
          if (idx !== lastSearchToolPartIndex) {
            return null;
          }
          if (part.state === "output-available") {
            const parsed = SearchActivitiesOutputSchema.safeParse(part.output);
            const resultFilters = parsed.success ? parsed.data.filters : null;
            const resultActivityIds = parsed.success ? parsed.data.activityIds : [];
            const isFiltersTurn = parsed.success && parsed.data.count === undefined && resultActivityIds.length === 0;
            const count = parsed.success && !isFiltersTurn ? resultActivityIds.length : undefined;
            const toolInput = "input" in part ? part.input : null;
            const summary = parsed.success ? buildSummaryLabel(i18n, isFiltersTurn ? undefined : resultActivityIds.length, parsed.data.filters, parsed.data.requestedLimit) : null;
            const retrievalStrategy = parsed.success ? parsed.data.retrievalStrategy : undefined;
            const retrievalReason = parsed.success ? parsed.data.retrievalReason : undefined;
            return <div key={`${message.id}-tool-${idx}`} className="flex flex-col gap-2" data-testid="ai-chat-tool-result">{isAdmin ? <details className="group/tool text-[11px] text-blue-900/50"><summary className="flex cursor-pointer list-none items-center gap-1.5 text-blue-900/50 hover:text-blue-900/70"><Search className="size-3" strokeWidth={2} aria-hidden={true} /><span>{i18n._("activity.search.ai.toolCall.label", undefined, {
                      message: "search_activities"
                    })}</span><ChevronDown className="size-3 transition-transform group-open/tool:rotate-180" aria-hidden={true} /></summary><div className="mt-2 flex flex-col gap-2"><div><div className="text-blue-900/50">{i18n._("activity.search.ai.toolCall.input", undefined, {
                        message: "Input"
                      })}</div><pre className="mt-1 overflow-x-auto rounded-md bg-white/70 p-2 font-mono text-[10px] break-words whitespace-pre-wrap text-blue-900/80">{JSON.stringify(toolInput ?? {}, null, 2)}</pre></div><div><div className="text-blue-900/50">{i18n._("activity.search.ai.toolCall.output", undefined, {
                        message: "Output"
                      })}</div><pre className="mt-1 overflow-x-auto rounded-md bg-white/70 p-2 font-mono text-[10px] break-words whitespace-pre-wrap text-blue-900/80">{JSON.stringify(part.output ?? {}, null, 2)}</pre></div></div></details> : null}{isAdmin && retrievalStrategy ? <div className="flex flex-col items-start gap-1"><span data-testid="ai-chat-retrieval-strategy" className="inline-flex w-fit items-center rounded-full bg-white/70 px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-blue-900/70 uppercase">{retrievalStrategy === "structural" ? "Structural" : "Subjective"}</span>{retrievalReason ? <p data-testid="ai-chat-retrieval-reason" className="max-w-[90%] text-[11px] leading-snug text-blue-900/50">{retrievalReason}</p> : null}</div> : null}{summary && resultFilters && !isActiveResult ? <Tooltip><TooltipTrigger asChild={true}><button type="button" onClick={() => onApplyResult(message.id, resultFilters, resultActivityIds)} data-testid="ai-chat-tool-summary-apply" aria-label={i18n._("activity.search.ai.results.applyResult", undefined, {
                    message: "Click to show on the grid"
                  })} className="group/apply flex items-center gap-1.5 self-start rounded-lg text-left text-xs text-blue-900/70 transition-colors hover:text-secondary"><RotateCcw className="size-3 shrink-0 opacity-0 transition-opacity group-hover/apply:opacity-100" strokeWidth={2.5} aria-hidden={true} /><span>{summary}</span></button></TooltipTrigger><TooltipContent>{i18n._("activity.search.ai.results.applyResult", undefined, {
                    message: "Click to show on the grid"
                  })}</TooltipContent></Tooltip> : summary && isActiveResult ? <Tooltip><TooltipTrigger asChild={true}><p className="cursor-default text-xs font-medium text-secondary" data-testid="ai-chat-tool-summary">{summary}</p></TooltipTrigger><TooltipContent>{i18n._("activity.search.ai.results.showingOnGrid", undefined, {
                    message: "Shown on the grid"
                  })}</TooltipContent></Tooltip> : summary ? <p className="text-xs text-blue-900/70" data-testid="ai-chat-tool-summary">{summary}</p> : null}{variant === "mobile" && count !== undefined && count > 0 && onCloseDrawer ? <button type="button" onClick={onCloseDrawer} data-testid="ai-chat-show-all" className="self-start rounded-full bg-secondary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-secondary/90">{i18n._("activity.search.ai.results.showAll", {
                  count
                }, {
                  message: "Show all {count} results"
                })}</button> : null}{resultFilters ? <ActivityAiResultFilterChips filters={resultFilters} live={isLatest} storedChips={storedFilterChips} /> : null}</div>;
          }
          if (part.state === "output-error") {
            if (assistantTextIsAnimating) {
              return null;
            }
            const rawError = (part as unknown as {
              error?: unknown;
            }).error;
            const errorMsg = isAdmin && rawError != null ? extractErrorMessage(rawError) : null;
            return <div key={`${message.id}-tool-${idx}`} data-testid="ai-chat-tool-error" className="flex flex-col items-start rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><p>{i18n._({
                  id: "activity.search.ai.tool.error",
                  message: "Search failed. Try rephrasing."
                })}</p>{errorMsg ? <pre className="mt-1 overflow-x-auto font-mono text-[10px] break-words whitespace-pre-wrap text-red-700/70">{errorMsg}</pre> : null}{canReply ? <button type="button" onClick={() => onRetry(message.id)} data-testid="ai-chat-tool-error-retry" className="mt-2 inline-flex items-center gap-1.5 self-start rounded-full bg-red-700 px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-red-700/90"><RotateCcw className="size-3" strokeWidth={2.5} aria-hidden={true} />{i18n._({
                  id: "activity.search.ai.error.retry",
                  message: "Retry"
                })}</button> : null}</div>;
          }
          return <p key={`${message.id}-tool-${idx}`} data-testid="ai-chat-tool-searching" className="inline-flex items-center gap-1.5 text-[11px] text-blue-900/50"><Loader2 className="size-3 animate-spin" aria-hidden={true} />{i18n._({
              id: "activity.search.ai.tool.searching",
              message: "Searching activities\u2026"
            })}</p>;
        }
        return null;
      };
      $[20] = assistantTextIsAnimating;
      $[21] = canReply;
      $[22] = i18n;
      $[23] = isActiveResult;
      $[24] = isAdmin;
      $[25] = isLatest;
      $[26] = lastSearchToolPartIndex;
      $[27] = lastTextPartIndex;
      $[28] = message.id;
      $[29] = onApplyResult;
      $[30] = onCloseDrawer;
      $[31] = onRetry;
      $[32] = storedFilterChips;
      $[33] = variant;
      $[34] = t3;
    } else {
      t3 = $[34];
    }
    t2 = message.parts.map(t3);
    $[4] = assistantTextIsAnimating;
    $[5] = canReply;
    $[6] = i18n;
    $[7] = isActiveResult;
    $[8] = isAdmin;
    $[9] = isLatest;
    $[10] = lastSearchToolPartIndex;
    $[11] = lastTextPartIndex;
    $[12] = message.id;
    $[13] = message.parts;
    $[14] = onApplyResult;
    $[15] = onCloseDrawer;
    $[16] = onRetry;
    $[17] = storedFilterChips;
    $[18] = variant;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  let t3;
  if ($[35] !== chatId || $[36] !== hasAnswer || $[37] !== message.id || $[38] !== message.metadata) {
    t3 = hasAnswer ? <AssistantAnswerFeedback chatId={chatId} messageId={message.id} traceId={(message.metadata as {
      traceId?: string;
    } | undefined)?.traceId} /> : null;
    $[35] = chatId;
    $[36] = hasAnswer;
    $[37] = message.id;
    $[38] = message.metadata;
    $[39] = t3;
  } else {
    t3 = $[39];
  }
  let t4;
  if ($[40] !== canReply || $[41] !== i18n || $[42] !== message || $[43] !== onReply || $[44] !== onRetry) {
    t4 = canReply ? <div className="flex justify-end gap-1 pr-1"><Tooltip><TooltipTrigger asChild={true}><button type="button" onClick={() => onRetry(message.id)} aria-label={i18n._("activity.search.ai.retryMessage", undefined, {
            message: "Retry this message"
          })} className="rounded-full p-1 text-blue-900/40 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-blue-50 hover:text-blue-900 focus-visible:opacity-100"><RotateCcw className="size-3.5" strokeWidth={2.25} aria-hidden={true} /></button></TooltipTrigger><TooltipContent side="left">{i18n._("activity.search.ai.retryMessage", undefined, {
            message: "Retry this message"
          })}</TooltipContent></Tooltip><Tooltip><TooltipTrigger asChild={true}><button type="button" onClick={() => onReply(message)} data-testid="ai-chat-assistant-reply" aria-label={i18n._("activity.search.ai.replyToMessage", undefined, {
            message: "Reply to this message"
          })} className="rounded-full p-1 text-blue-900/40 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-blue-50 hover:text-blue-900 focus-visible:opacity-100"><CornerDownRight className="size-3.5" strokeWidth={2.25} aria-hidden={true} /></button></TooltipTrigger><TooltipContent side="left">{i18n._("activity.search.ai.replyToMessage", undefined, {
            message: "Reply to this message"
          })}</TooltipContent></Tooltip></div> : null;
    $[40] = canReply;
    $[41] = i18n;
    $[42] = message;
    $[43] = onReply;
    $[44] = onRetry;
    $[45] = t4;
  } else {
    t4 = $[45];
  }
  let t5;
  if ($[46] !== t2 || $[47] !== t3 || $[48] !== t4) {
    t5 = <>{t2}{t3}{t4}</>;
    $[46] = t2;
    $[47] = t3;
    $[48] = t4;
    $[49] = t5;
  } else {
    t5 = $[49];
  }
  return t5;
});

/**
 * User-facing result summary, localized via i18n. Mirrors the server tool's
 * `buildSummary` phrasing but renders the constraint tail + count through the
 * i18n plugin so nl/fr come from the locale catalogs instead of hardcoded strings.
 */
function buildSummaryLabel(i18n: ReturnType<typeof useI18n>, /** `undefined` = a filters-only turn: the grid owns the total. */
count: number | undefined, filters: SearchActivitiesFilters, requestedLimit?: number): string {
  const constraints: string[] = [];
  if (filters.participants !== undefined) {
    constraints.push(i18n._('activity.search.ai.summary.partySize', {
      count: filters.participants
    }, {
      message: '{count} pers.'
    }));
  }
  if (filters.locationLabel) constraints.push(filters.locationLabel);
  if (filters.minBudget !== undefined && filters.maxBudget !== undefined) {
    constraints.push(i18n._('activity.search.ai.summary.budgetRange', {
      min: filters.minBudget,
      max: filters.maxBudget
    }, {
      message: '€{min}–€{max} p.p.'
    }));
  } else if (filters.maxBudget !== undefined) {
    constraints.push(i18n._('activity.search.ai.summary.maxBudget', {
      budget: filters.maxBudget
    }, {
      message: '≤ €{budget} p.p.'
    }));
  } else if (filters.minBudget !== undefined) {
    constraints.push(i18n._('activity.search.ai.summary.minBudget', {
      budget: filters.minBudget
    }, {
      message: '≥ €{budget} p.p.'
    }));
  }
  const tail = constraints.length > 0 ? ` (${constraints.join(', ')})` : '';
  if (count === undefined) {
    return i18n._('activity.search.ai.summary.filtersApplied', {
      tail
    }, {
      message: 'Filters applied{tail} — the list shows every match.'
    });
  }
  if (count === 0) {
    return i18n._('activity.search.ai.summary.empty', {
      tail
    }, {
      message: 'No matches yet{tail}. Try widening the budget or location.'
    });
  }
  if (requestedLimit !== undefined && count < requestedLimit) {
    return i18n._('activity.search.ai.summary.shortfall', {
      count,
      requested: requestedLimit,
      tail
    }, {
      message: 'Only {count, plural, one {# activity matches} other {# activities match}}{tail} — fewer than the {requested} you asked for.'
    });
  }
  return i18n._('activity.search.ai.summary.count', {
    count,
    tail
  }, {
    message: '{count, plural, one {# matching activity} other {# matching activities}}{tail}.'
  });
}
function shouldRenderChatMessage(message: UIMessage): boolean {
  if (message.role === 'user') return collectUserText(message).length > 0;
  if (message.role !== 'assistant') return false;
  return message.parts.some(p => p.type === 'text' || isToolUIPart(p) && getToolName(p) === 'search_activities');
}

/**
 * Index of the LAST `search_activities` tool part in a turn. A failing tool is
 * retried within one streaming turn (`stepCountIs`), appending a fresh tool part
 * per attempt — only the last one reflects the turn's final state, so both the
 * render loop and the panel-level loading derivation collapse to it.
 */
function getLastSearchToolPartIndex(message: UIMessage): number {
  let index = -1;
  for (let i = 0; i < message.parts.length; i++) {
    const part = message.parts[i];
    if (part && isToolUIPart(part) && getToolName(part) === 'search_activities') index = i;
  }
  return index;
}
function collectUserText(message: UIMessage): string {
  return message.parts.filter(p => p.type === 'text').map(p => p.type === 'text' ? p.text : '').join('');
}
function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const m = error.message;
    if (typeof m === 'string') return m;
  }
  return 'Unknown error';
}

/**
 * Seeds the grid URL overlay from the latest resolved AI search result the moment
 * it becomes active — so a fresh turn's extracted participants/budget/categories
 * reach the grid + drawer without the user clicking a past result. Without this the
 * grid falls back to `MIN_PARTICIPANTS_FALLBACK` (20) and the "filters changed"
 * note fires spuriously (result filters ≠ empty URL) on every fresh chat.
 *
 * Observing the panel's reactive `searchResults` covers BOTH cases at once: a live
 * turn's tool result landing (streaming) and a reopened/deep-linked chat's
 * transcript hydration (`setMessages`) each push a new latest result through here.
 *
 * Lives in a hook (the guide's accepted escape hatch, mirroring
 * `useTrackAssistantTurns`) so the component never holds a raw `useEffect`. The
 * dependency array is the dedupe: `chatId::messageId` fires it once per new result
 * or thread switch (chat id in the key so re-opening a chat re-seeds even when a
 * hydrated message carries an empty id), while the URL write it makes never touches
 * `chatId`/`messages`/`appliedResultMessageId` — so it can't refire itself.
 *
 * Skipped while a past result is pinned (`appliedResultMessageId`) — manual "go
 * back" nav (`goToSearchResult`) owns the URL then. The pin resets to `null` on
 * every new turn, so a fresh result always applies.
 */
function useAutoApplyAiResult(input) {
  const $ = _c(7);
  const result = input.latestResult;
  const dedupeKey = result ? `${input.chatId}::${result.messageId}` : null;
  let t0;
  if ($[0] !== dedupeKey || $[1] !== input || $[2] !== result) {
    t0 = () => {
      if (!result || dedupeKey === null) {
        return;
      }
      if (input.appliedResultMessageId !== null) {
        return;
      }
      input.applyAiResult(result.filters, result.activityIds, null);
    };
    $[0] = dedupeKey;
    $[1] = input;
    $[2] = result;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== dedupeKey || $[5] !== input.appliedResultMessageId) {
    t1 = [dedupeKey, input.appliedResultMessageId];
    $[4] = dedupeKey;
    $[5] = input.appliedResultMessageId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  useEffect(t0, t1);
}
function isRateLimitError(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === 'object' && error !== null && 'statusCode' in error) {
    if (error.statusCode === 429) return true;
  }
  const msg = extractErrorMessage(error).toLowerCase();
  return msg.includes('rate') || msg.includes('429') || msg.includes('free tier') || msg.includes('quota');
}
type RateLimitInfo = {
  /** Unix epoch ms when the tripped window frees up. */
  readonly reset: number;
  /** Bucket cap (e.g. 8) and window (e.g. 60s); null for upstream/provider 429s. */
  readonly limit: number | null;
  readonly windowSeconds: number | null;
};

/** Parse our own `/api/ai/chat` 429 JSON body off the error. Returns null for
 *  provider rate limits (no structured body) — caller falls back to the raw text. */
function parseRateLimitInfo(error: unknown): RateLimitInfo | null {
  let data: unknown;
  try {
    data = JSON.parse(extractErrorMessage(error));
  } catch {
    return null;
  }
  if (typeof data !== 'object' || data === null || !('reset' in data)) return null;
  const reset = typeof data.reset === 'number' ? data.reset : null;
  if (reset === null) return null;
  return {
    reset,
    limit: 'limit' in data && typeof data.limit === 'number' ? data.limit : null,
    windowSeconds: 'windowSeconds' in data && typeof data.windowSeconds === 'number' ? data.windowSeconds : null
  };
}
const RateLimitDetail: React.FC<{
  info: RateLimitInfo;
}> = t0 => {
  const $ = _c(17);
  const {
    info
  } = t0;
  const i18n = useI18n();
  let t1;
  if ($[0] !== info.reset) {
    t1 = () => Math.max(0, Math.ceil((info.reset - Date.now()) / 1000));
    $[0] = info.reset;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [secondsLeft, setSecondsLeft] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== info.reset) {
    t2 = () => {
      if (info.reset <= 0) {
        return;
      }
      const tick = () => setSecondsLeft(Math.max(0, Math.ceil((info.reset - Date.now()) / 1000)));
      tick();
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    };
    t3 = [info.reset];
    $[2] = info.reset;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== i18n || $[6] !== info.limit || $[7] !== info.windowSeconds) {
    t4 = info.limit !== null && info.windowSeconds !== null ? <span>{i18n._({
        id: "activity.search.ai.error.rateLimit.cap",
        message: "Limit: {limit} requests / {windowSeconds}s",
        values: {
          limit: info.limit,
          windowSeconds: info.windowSeconds
        }
      })}</span> : null;
    $[5] = i18n;
    $[6] = info.limit;
    $[7] = info.windowSeconds;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== i18n || $[10] !== secondsLeft) {
    t5 = secondsLeft > 0 ? i18n._({
      id: "activity.search.ai.error.rateLimit.retryIn",
      message: "Try again in {seconds}s",
      values: {
        seconds: secondsLeft
      }
    }) : i18n._({
      id: "activity.search.ai.error.rateLimit.retryNow",
      message: "You can try again now"
    });
    $[9] = i18n;
    $[10] = secondsLeft;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <span className="font-medium">{t5}</span>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <div className="flex flex-col gap-0.5 text-[11px] text-red-900/70">{t4}{t6}</div>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};